import React, { useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";

/**
 * Google Places autocomplete input.
 * Calls onSelect({ address, lat, lng, placeId }) when the user picks a result.
 * Assumes the Places library is already loaded (via @react-google-maps/api
 * useJsApiLoader called elsewhere in the page).
 */
const PlacesAutocomplete = ({
  value,
  onChange,
  onSelect,
  placeholder = "Search address",
  disabled = false,
}) => {
  const [predictions, setPredictions] = useState([]);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const autocompleteSvcRef = useRef(null);
  const placesSvcRef = useRef(null);
  const sessionTokenRef = useRef(null);

  useEffect(() => {
    if (
      !autocompleteSvcRef.current &&
      window.google &&
      window.google.maps &&
      window.google.maps.places
    ) {
      autocompleteSvcRef.current = new window.google.maps.places.AutocompleteService();
      const dummyDiv = document.createElement("div");
      placesSvcRef.current = new window.google.maps.places.PlacesService(dummyDiv);
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    }
  });

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const requestPredictions = (input) => {
    if (!autocompleteSvcRef.current || !input || input.length < 3) {
      setPredictions([]);
      return;
    }
    autocompleteSvcRef.current.getPlacePredictions(
      {
        input,
        sessionToken: sessionTokenRef.current,
      },
      (results) => {
        setPredictions(results || []);
      },
    );
  };

  const handleSelect = (prediction) => {
    if (!placesSvcRef.current) return;
    placesSvcRef.current.getDetails(
      {
        placeId: prediction.place_id,
        fields: ["formatted_address", "geometry", "place_id", "name"],
        sessionToken: sessionTokenRef.current,
      },
      (place, status) => {
        if (
          status !== window.google.maps.places.PlacesServiceStatus.OK ||
          !place ||
          !place.geometry
        ) {
          return;
        }
        const address = place.formatted_address || prediction.description;
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
        setOpen(false);
        setPredictions([]);
        if (typeof onSelect === "function") {
          onSelect({ address, lat, lng, placeId: prediction.place_id });
        }
      },
    );
  };

  return (
    <div className="autocomplete-input" ref={containerRef}>
      <Form.Control
        type="text"
        value={value || ""}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          onChange(e.target.value);
          requestPredictions(e.target.value);
          setOpen(true);
        }}
      />

      {open && predictions.length > 0 && (
        <ul className="autocomplete-suggestions">
          {predictions.slice(0, 10).map((p) => (
            <li
              key={p.place_id}
              className="autocomplete-suggestion"
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(p);
              }}
            >
              {p.description}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PlacesAutocomplete;
